import React from 'react';
import {
  Link,
  RouteComponentProps,
  matchPath,
  withRouter,
  generatePath,
} from 'react-router-dom';
import { NavbarBrand, Button, Spinner } from 'reactstrap';
import OrganizationProvider from '../Providers/Organizations';
import { OrganizationContext } from '../../contexts/organization';
import PractitionerProvider from '../Providers/Practitioner';
import { PractitionerContext } from '../../contexts/practitioner';
import PatientProvider from '../Providers/Patient';
import { PatientContext } from '../../contexts/patient';

type CrumbProps = {
  id: string;
  path: string | string[];
  display?: string;
  linkType?: 'organization' | 'patient' | 'practitioner';
  to?: string;
  exact?: boolean;
};

const DefaultCrumb = ({
  path,
  display,
  loading,
}: {
  path: string;
  display?: string;
  loading?: boolean;
}) => {
  return (
    <>
      {' '}
      <Button color="primary" outline tag={Link} to={path}>
        {loading ? <Spinner size="sm" /> : display}
      </Button>{' '}
    </>
  );
};

const OrganizationCrumb = ({
  organizationId,
  path,
}: {
  organizationId: string;
  path: string;
}) => {
  return (
    <OrganizationProvider organizationId={organizationId}>
      <OrganizationContext.Consumer>
        {({ loading, getOrganizationName }) => (
          <DefaultCrumb
            display={getOrganizationName()}
            path={path}
            loading={loading}
          />
        )}
      </OrganizationContext.Consumer>
    </OrganizationProvider>
  );
};

const PatientCrumb = ({
  patientId,
  path,
}: {
  patientId: string;
  path: string;
}) => {
  return (
    <PatientProvider patientId={patientId}>
      <PatientContext.Consumer>
        {({ loading, getDisplayName }) => (
          <DefaultCrumb
            display={getDisplayName()}
            path={path}
            loading={loading}
          />
        )}
      </PatientContext.Consumer>
    </PatientProvider>
  );
};

const PractitionerCrumb = ({
  practitionerId,
  path,
}: {
  practitionerId: string;
  path: string;
}) => {
  return (
    <PractitionerProvider practitionerId={practitionerId}>
      <PractitionerContext.Consumer>
        {({ loading, getDisplayName }) => (
          <DefaultCrumb
            display={getDisplayName()}
            path={path}
            loading={loading}
          />
        )}
      </PractitionerContext.Consumer>
    </PractitionerProvider>
  );
};

const Crumb = withRouter((props: RouteComponentProps<{}> & CrumbProps) => {
  const paths = Array.isArray(props.path) ? props.path : [props.path];
  const matchs = paths
    .map(path =>
      matchPath<{
        practitionerId: string;
        organizationId: string;
        patientId: string;
      }>(props.location.pathname, {
        path,
        exact: props.exact,
      }),
    )
    .filter(p => !!p);
  const match = matchs[0] || null;
  if (!match) return null;
  const gPath = generatePath(props.to ? props.to : match.path, match.params);

  if (props.linkType === 'organization')
    return (
      <OrganizationCrumb
        organizationId={match.params.organizationId}
        path={gPath}
      />
    );

  if (props.linkType === 'patient')
    return <PatientCrumb patientId={match.params.patientId} path={gPath} />;

  if (props.linkType === 'practitioner')
    return (
      <PractitionerCrumb
        practitionerId={match.params.practitionerId}
        path={gPath}
      />
    );

  return <DefaultCrumb path={gPath} display={props.display} />;
});

const Breadcrumb = (props: { links: CrumbProps[] }) => {
  const render = props.links.map(l => <Crumb key={l.id} {...l} />);
  return <NavbarBrand tag="span">{render}</NavbarBrand>;
};

const NavCrumbs = () => {
  const links: CrumbProps[] = [
    {
      id: '0',
      path: ['/organization'],
      display: '/',
      to: '/organization',
    },
    {
      id: '1',
      path: ['/', '/profile'],
      display: 'Organizations',
      to: '/organization',
      exact: true,
    },
    {
      id: '2',
      path: ['/report'],
      display: 'Organizations',
      to: '/organization',
    },
    {
      id: '3',
      path: '/organization/:organizationId',
      linkType: 'organization',
    },
    {
      id: '4',
      path: '/organization/:organizationId/patient',
      to: '/organization/:organizationId',
      display: '/',
    },
    {
      id: '5',
      path: '/organization/:organizationId/practitioner',
      display: '/',
    },
    {
      id: '6',
      path: '/organization/:organizationId/patient/:patientId',
      linkType: 'patient',
    },
    {
      id: '7',
      path: '/organization/:organizationId/practitioner/:practitionerId',
      linkType: 'practitioner',
    },
  ];

  return <Breadcrumb links={links} />;
};

export default NavCrumbs;
